'use client'

import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useSyncExternalStore } from 'react'
import { ArrowRightIcon } from 'lucide-react'

import { track } from '@/lib/analytics/track'
import { shouldShowProductHuntBanner } from '@/lib/product-hunt-offer'

// Routes through a server handler that sends the visitor to the right place by
// session state: signed-out → /sign-up (+ campaign-intent cookie for onboarding);
// signed-in + onboarded → their brand's integrations page directly. Keeping the
// decision server-side means an already-onboarded user lands on integrations too,
// not just brand-new sign-ups (see app/go/chatgpt-ads/route.ts).
const BANNER_HREF = '/go/chatgpt-ads'

const subscribeNever = () => () => {}

/**
 * Homepage announcement bar promoting ChatGPT Ads. Sits flush above the dark
 * hero (matching its background) with a single primary-colored accent on the
 * CTA so it reads as an invitation, not a loud takeover. Clicking it routes the
 * visitor to the integrations page where the ChatGPT Ads card leads the grid —
 * via /sign-up + onboarding for new users, or directly for onboarded ones.
 */
export function ChatGptAdsBanner() {
  const pathname = usePathname() ?? ''
  // Product Hunt visitors get the launch-offer bar instead; two stacked
  // announcement bars push the hero down. Server render keeps this bar, so
  // for flagged visitors it swaps for the offer bar on hydration.
  const productHuntBannerShown = useSyncExternalStore(
    subscribeNever,
    () => shouldShowProductHuntBanner(pathname),
    () => false,
  )
  if (productHuntBannerShown) return null

  return (
    <Link
      href={BANNER_HREF}
      prefetch={false}
      onClick={() => {
        track(
          'marketing_cta_click',
          {
            cta_text: 'Advertise on ChatGPT',
            cta_location: 'chatgpt_ads_banner',
          },
        )
      }}
      // Ink rather than white: the banner sits directly above the hero
      // painting, and a white strip read as a seam cutting across the top of
      // the page. The ink is the same tone the sky dissolves into.
      className="group/banner bg-ink flex items-center justify-center gap-x-2.5 gap-y-1 border-b border-white/10 px-4 py-4 text-center text-sm text-white/70 transition hover:text-white/90"
    >
      <span>
        <span className="font-semibold text-white">New —</span> Advertise on
        ChatGPT and get your brand in front of ChatGPT users.
      </span>
      <span className="text-primary hidden shrink-0 items-center gap-1 font-semibold underline-offset-4 group-hover/banner:underline sm:inline-flex">
        Get started
        <ArrowRightIcon
          className="size-4 transition-transform group-hover/banner:translate-x-0.5"
          aria-hidden
        />
      </span>
    </Link>
  )
}
